import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTenant } from '../config/tenantConfig';
import { useDarkMode } from '../DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useSidePanel } from '../context/SidePanelContext';
import { useRoleContext } from '../auth/RoleContext';
import { useWhiteLabel } from '../context/WhiteLabelContext';
import { SIDEBAR_ITEM_KEYS } from '../auth/permissionsConfig';
import Swal from 'sweetalert2';
import SupportChatAgent from '../components/SupportChatAgent';
import LandingGuideAgent from '../components/LandingGuideAgent';
import SystemStatusIndicator from '../components/SystemStatusIndicator';
import GlassTopBar from '../components/GlassTopBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import api from '../lib/api';
import { getBranding } from '../config/brandingConfig';
import TrialBanner from '../components/subscription/TrialBanner';
import {
  Home,
  FileText,
  Users,
  CheckCircle,
  LogOut,
  Menu,
  X,
  Bell,
  Sun,
  Moon,
  Compass,
  MessageSquare,
  DollarSign,
  ClipboardList,
  FileCheck,
  FileSignature,
  Building2,
  Banknote,
  Store,
  Settings,
  FlaskConical
} from 'lucide-react';

/**
 * Layout pour l'application Customer
 * UX simplifiée pour les clients
 */
const CustomerLayout = ({ children }) => {
  const { showSidePanel: isSidebarOpen, setShowSidePanel: setIsSidebarOpen, isMobile } = useSidePanel();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadChatMessages, setUnreadChatMessages] = useState(0);
  const [trialInfo, setTrialInfo] = useState(null);
  const navigate = useNavigate();
  const { tenantName, theme, getTenantPath } = useTenant();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user: authUser, isAuthenticated, logout } = useAuth();
  const { hasAccessTo, permissionsLoaded } = useRoleContext();
  const currentUser = useSelector(state => state.userManagement.currentUser);
  const validationStats = useSelector(state => state.validation?.stats);
  const displayUser = currentUser || authUser || null;
  const displayName = displayUser
    ? `${displayUser.firstName || ''} ${displayUser.lastName || ''}`.trim() || displayUser.email
    : '';
  const displayCompany =
    displayUser?.companyName || displayUser?.company?.name || 'Customer Portal';
  const displayRole = displayUser?.category || displayUser?.role || 'Customer';
  console.log('[CustomerLayout] displayUser:', displayUser?.email, 'category:', displayUser?.category, 'role:', displayUser?.role, 'displayRole:', displayRole);
  const location = useLocation();

  // Get global white-label branding (set by admin - applies to ALL pages)
  const { branding: globalBranding, currentProfileCode } = useWhiteLabel();
  const hasWhiteLabel = currentProfileCode && currentProfileCode !== 'halisoft';

  // Check if user is a HaliAgent user - show HaliAgent branding
  const isHaliAgentUser = (() => {
    if (!displayUser) return false;
    const userCategory = (displayUser.category || displayUser.userType || displayUser.user_type || '').toLowerCase();
    const userRole = (displayUser.role || '').toLowerCase();
    const userType = (displayUser.type || '').toLowerCase();

    return userCategory === 'halitrade' ||
           userCategory === 'halitrade_user' ||
           userCategory === 'haliagent' ||
           userCategory === 'agent' ||
           userRole === 'agent' ||
           userRole.includes('halitrade') ||
           userRole.includes('haliagent') ||
           userType === 'agent' ||
           userType === 'halitrade' ||
           userType === 'haliagent';
  })();

  // Get branding - prioritize global white-label if set by admin, otherwise use user-type based branding
  const defaultBranding = getBranding(isHaliAgentUser ? 'HALIAGENT' : 'HALIFLOW');
  const activeLogo = hasWhiteLabel
    ? (globalBranding.logoUrl || `${process.env.PUBLIC_URL || ''}${defaultBranding.logo}`)
    : `${process.env.PUBLIC_URL || ''}${defaultBranding.logo}`;
  const activeBrandName = hasWhiteLabel ? globalBranding.companyName : defaultBranding.info.name;
  const activeBrandSubtitle = hasWhiteLabel ? globalBranding.tagline : defaultBranding.info.subtitle;
  const activeSidebarSubtitle = hasWhiteLabel ? globalBranding.tagline : defaultBranding.info.subtitle;
  const activePrimaryColor = hasWhiteLabel
    ? globalBranding.primaryColor
    : (isHaliAgentUser ? defaultBranding.colors.primary : theme.primary);
  // Get user's company name for sidebar display - use white-label company name if set
  const userCompanyName = hasWhiteLabel
    ? globalBranding.companyName
    : (displayUser?.companyName || displayUser?.company?.name || null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // On mobile, sidebar overlays — no margin shift needed
  const sidebarWidth = isMobile ? 0 : (isSidebarOpen ? (sidebarCollapsed ? 80 : 256) : 0);
  useEffect(() => {
    if (!isSidebarOpen) {
      setSidebarCollapsed(false);
    }
  }, [isSidebarOpen]);

  // Fetch trial info — check all modules and show the one expiring soonest
  useEffect(() => {
    if (!isAuthenticated) return;
    const modules = ['risk-analysis', 'hali-leads', 'contract-analyzer', 'invoice-financing-check', 'past-trade-studio', 'global'];
    Promise.all(
      modules.map(m => api.get(`/api/subscriptions/current?module=${m}`).then(r => r.data).catch(() => null))
    ).then(results => {
      const trial = results
        .filter(r => r?.is_trial && r?.trial_days_remaining != null)
        .sort((a, b) => a.trial_days_remaining - b.trial_days_remaining)[0];
      if (trial) setTrialInfo({ daysRemaining: trial.trial_days_remaining, module: trial.module });
    });
  }, [isAuthenticated]);

  const menuItems = [
    {
      icon: Home,
      label: 'Dashboard',
      path: getTenantPath('dashboard'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.DASHBOARD
    },
    {
      icon: FlaskConical,
      label: 'HaliLab',
      path: '/halilab/dashboard',
      badge: null,
      itemKey: 'HALILAB',
      color: '#7c3aed' // Purple color for HaliLab
    },
    {
      icon: MessageSquare,
      label: 'Messages',
      path: getTenantPath('messages'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.MESSAGES
    },
    {
      icon: DollarSign,
      label: 'My Invoices',
      path: getTenantPath('invoice-financing'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.INVOICE_FINANCING
    },
    {
      icon: Store,
      label: 'Marketplace',
      path: getTenantPath('marketplace'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.MARKETPLACE
    },
    {
      icon: ClipboardList,
      label: 'My Submissions',
      path: getTenantPath('my-submissions'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.MY_SUBMISSIONS
    },
    {
      icon: FileSignature,
      label: 'My Termsheets',
      path: getTenantPath('my-termsheets'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.MY_TERMSHEETS
    },
    {
      icon: FileCheck,
      label: 'My Contracts',
      path: getTenantPath('my-contracts'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.MY_CONTRACTS
    },
    {
      icon: Building2,
      label: 'My Companies',
      path: getTenantPath('my-companies'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.MY_COMPANIES
    },
    {
      icon: Banknote,
      label: 'Financing Status',
      path: getTenantPath('factor-tools/financing-dashboard'),
      itemKey: SIDEBAR_ITEM_KEYS.FACTOR_TOOLS
    },
    {
      icon: FileText,
      label: 'My Company',
      path: getTenantPath('company'),
      badge: validationStats?.revisionRequired > 0 ? validationStats.revisionRequired : null,
      itemKey: SIDEBAR_ITEM_KEYS.MY_COMPANY
    },
    {
      icon: Users,
      label: 'Trade Partners',
      path: getTenantPath('trade-partners'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.TRADE_PARTNERS
    },
    {
      icon: CheckCircle,
      label: 'Validation Status',
      path: getTenantPath('validation-status'),
      badge: validationStats?.pending > 0 ? validationStats.pending : null,
      itemKey: SIDEBAR_ITEM_KEYS.VALIDATION_STATUS
    },
    {
      icon: Compass,
      label: 'Onboarding Choice',
      path: getTenantPath('onboarding-choice'),
      badge: null,
      itemKey: SIDEBAR_ITEM_KEYS.ONBOARDING_CHOICE
    },
    {
      icon: FileCheck,
      label: 'LC DocCheck',
      path: getTenantPath('lctradedoccheck'),
      badge: null,
      itemKey: null // Visible for all customers
    },
    {
      icon: Settings,
      label: 'Settings',
      path: getTenantPath('settings'),
      badge: null,
      itemKey: null // Always visible for all users
    }
  ];

  // Keep agentMenuItems for backwards compatibility
  const agentMenuItems = menuItems;

  const iconColorMap = {
    Dashboard: '#60a5fa',
    Messages: '#f472b6',
    'My Invoices': '#facc15',
    'My Submissions': '#a855f7',
    'My Termsheets': '#22d3ee',
    'My Contracts': '#34d399',
    'My Companies': '#22d3ee',
    'My Company': '#22d3ee',
    'Trade Partners': '#34d399',
    'Validation Status': '#ef4444',
    'Onboarding Choice': '#f472b6',
    'Settings': '#f97316'
  };
  // Filter menu items based on permissions
  console.log('[CustomerLayout] Filtering menu items - total:', agentMenuItems.length, 'permissionsLoaded:', permissionsLoaded);
  const filteredMenuItems = agentMenuItems.filter(
    (item) => {
      const allowed = !item.itemKey || hasAccessTo(item.itemKey);
      if (!allowed) {
        console.log('[CustomerLayout] Item filtered out:', item.label, 'itemKey:', item.itemKey);
      }
      return allowed;
    }
  );
  console.log('[CustomerLayout] Filtered menu items:', filteredMenuItems.length);

  const sidebarMenuItems = filteredMenuItems.map((item) => ({
    ...item,
    color: iconColorMap[item.label] || theme.primary
  }));
  const handleSidebarNavigate = (path) => {
    navigate(path);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBrandClick = () => {
    navigate(getTenantPath('dashboard'));
  };

  const handleNotificationsClick = () => {
    setIsChatOpen((prev) => !prev);
  };

  // Tenant guard: Log user access for debugging
  // Allow all authenticated users to access HaliFlow customer portal
  // (HaliTrade users share similar customer features)
  useEffect(() => {
    console.log('[CustomerLayout] Access check - isAuthenticated:', isAuthenticated, 'user category:', authUser?.category, 'tenant:', authUser?.tenant);

    if (isAuthenticated && authUser) {
      const userCategory = authUser.category || authUser.role || '';
      const userTenant = authUser.tenant || '';
      console.log('[CustomerLayout] ✅ User access granted - category:', userCategory, 'tenant:', userTenant);
    }
  }, [isAuthenticated, authUser]);

  // Poll for unread chat messages
  useEffect(() => {
    const chatSessionId = localStorage.getItem('liveChatSessionId');
    const lastReadMessageId = parseInt(localStorage.getItem('liveChatLastReadMessageId') || '0', 10);

    if (!chatSessionId) {
      setUnreadChatMessages(0);
      return;
    }

    const pollUnreadCount = async () => {
      try {
        const response = await api.get(`/api/support/chat/messages/${chatSessionId}`);

        if (response.data.success && response.data.messages) {
          // Count support messages that are newer than lastReadMessageId
          const currentLastRead = parseInt(localStorage.getItem('liveChatLastReadMessageId') || '0', 10);
          const newSupportMessages = response.data.messages.filter(
            msg => msg.sender === 'support' && msg.id > currentLastRead
          );
          setUnreadChatMessages(newSupportMessages.length);
        }
      } catch (error) {
        // Silently handle errors - session may not exist
        console.log('[CustomerLayout] Error polling unread count:', error.message);
      }
    };

    // Poll every 30 seconds (reduced from 5s to lower API load)
    const interval = setInterval(pollUnreadCount, 30000);

    // Initial fetch
    pollUnreadCount();

    return () => clearInterval(interval);
  }, [isChatOpen]); // Re-run when chat opens/closes to update count

  const handleLogout = async () => {
    // Logout is a deliberate one-click action — no confirmation prompt.
    // The previous SweetAlert confirmation blocked the e2e security suite
    // (which expected click → immediate token clear) and added friction for
    // users who already clicked "Logout" on purpose.
    logout();
    navigate('/haliagent/login');
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <GlassTopBar
        logoSrc={activeLogo}
        brandName={activeBrandName}
        brandSubtitle={activeBrandSubtitle}
        displayName={displayName}
        userEmail={displayUser?.email || ''}
        userCategory={displayUser?.category || undefined}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        isSidebarCollapsed={!isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onBrandClick={handleBrandClick}
        notifications={(validationStats?.pending || 0) + (validationStats?.revisionRequired || 0) + unreadChatMessages}
        onNotificationsClick={handleNotificationsClick}
        onLogout={handleLogout}
        borderColor={activePrimaryColor + '20'}
        primaryColor={activePrimaryColor}
      />

      <div className="flex">
        {/* Sidebar */}
        <CollapsibleSidebar
          className={!isSidebarOpen ? 'sidebar-hidden' : ''}
          style={{ top: '96px' }}
          logoSrc={activeLogo}
          brandName={activeBrandName}
          subtitle={activeSidebarSubtitle}
          whiteLabelCompanyName={userCompanyName}
          platformName="HaliTrade"
          viewBadge="Customer View"
          menuItems={sidebarMenuItems}
          activePath={location.pathname}
          onNavigate={handleSidebarNavigate}
          onLogoClick={() => navigate(getTenantPath('dashboard'))}
          onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
          primaryColor={activePrimaryColor}
          darkMode={darkMode}
        />

        {/* Main Content */}
        <main
          className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
          style={{ marginLeft: sidebarWidth }}
        >
          {trialInfo && (
            <TrialBanner daysRemaining={trialInfo.daysRemaining} module={trialInfo.module} />
          )}
          <div className="p-6 lg:p-8">
            {children || <Outlet />}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Support Chat Agent */}
      {isChatOpen && (
        <div className="fixed bottom-4 right-4 z-50">
          <SupportChatAgent initiallyOpen={true} onOpenChange={(open) => { if (!open) setIsChatOpen(false); }} />
        </div>
      )}

      <LandingGuideAgent darkMode={darkMode} theme="haliflow" position="left" />
    </div>
  );
};

export default CustomerLayout;
